// src/components/Form/employeedoctype.jsx
// UPDATED: Handle split shifts in default_shift display (from backend).
// - In ShiftModal: Display multiple time_slots if present in assigned_schedule.default_shift.
// - Fallback sample data updated to show split shift example.
// - Attendance tab: Default shift clickable shows full slots.
// - Full code provided, no lines missing.

import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaUserTie, FaArrowLeft, FaEdit, FaComment, FaClock, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaBriefcase, FaIdCard, FaDollarSign, FaUsers, FaTimes, FaSearch, FaBell, FaUniversity, FaUmbrellaBeach, FaCalendarCheck, FaClipboardList, FaFileInvoiceDollar, FaChevronUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
const EmployeeDocType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [allAttendanceData, setAllAttendanceData] = useState({}); // State for all months' attendance data, object with month keys
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Global State
  const { baseUrl, configLoading } = useContext(UserContext);

  const [currency, setCurrency] = useState('AED'); // Set to AED based on sample data
  // Modal state only for Default Shift
  const [showShiftModal, setShowShiftModal] = useState(false);

  // Roaster App Tab State
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const displayedSpecialDays = React.useMemo(() => {
    if (!employee || !employee.assigned_schedule) return [];
    const schedule = employee.assigned_schedule;
    const combined = new Map();

    // Add Special Days from Rule
    if (schedule.special_days) {
      schedule.special_days.forEach(sd => {
        const key = `${sd.date}-${sd.description}`;
        combined.set(key, { ...sd, source: 'rule' });
      });
    }

    // Add Special Day Assignments (Overrides)
    if (schedule.special_day_assignments) {
      schedule.special_day_assignments.forEach(sd => {
        const key = `${sd.date}-${sd.description}`;
        const existing = combined.get(key) || {};
        combined.set(key, { ...existing, ...sd, source: 'assignment' });
      });
    }

    return Array.from(combined.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [employee]);

  // Initial Data Fetching
  useEffect(() => {
    if (!configLoading) {
      const initData = async () => {
        await fetchCurrency();
        if (id) {
          await fetchEmployee();
        }
      };
      initData();
    }
  }, [baseUrl, configLoading, id]);

  // Fetch currency from system settings
  const fetchCurrency = async () => {
    try {
      const url = baseUrl ? `${baseUrl}/api/settings` : '/api/settings';
      const response = await axios.get(url);
      const settingsData = response.data;
      const currencyCode = settingsData.currency || 'AED'; // Default to AED for sample
      const currencySymbol = getCurrencySymbol(currencyCode);
      setCurrency(currencySymbol);
    } catch (err) {
      console.error('Error fetching currency settings:', err);
      setCurrency('AED');
    }
  };

  // Helper function to get currency symbol based on code
  const getCurrencySymbol = (code) => {
    const symbols = {
      'USD': '$',
      'INR': '₹',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'AED': 'AED',
    };
    return symbols[code] || code;
  };

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching employee with ID:', id, 'Base URL:', baseUrl);
      const url = baseUrl ? `${baseUrl}/api/add-employee/${id}` : `/api/add-employee/${id}`;
      console.log('Employee fetch URL:', url);
      const response = await axios.get(url);
      console.log('Employee fetch response:', response.data);
      setEmployee(response.data);
    } catch (err) {
      console.error('Error fetching employee:', err);
      setError(err.response?.data?.error || 'Failed to fetch employee details. Please try again.');
      // Fallback to sample data for demonstration if fetch fails - UPDATED: Split shift example
      setEmployee({
        _id: id,
        name: 'rohinth',
        salutation: 'Mr.',
        gender: 'Male',
        dateOfBirth: '2026-12-12',
        status: 'Active',
        maritalStatus: 'Single',
        nationality: 'Dubai',
        idNumber: '23534646745',
        idExpiry: '2026-12-12',
        dateOfJoining: '2026-01-01',
        company: 'POS 8',
        employeeDesignation: 'Kitchen',
        employeeType: 'part Time',
        email: 'rohinth@gmail.com',
        phoneNumber: '+971345346457',
        address: 'sdgdsfgdf',
        basicSalary: '325354',
        totalSalary: '719497',
        profileImage: '', // Empty for initials fallback
        bankName: 'State Bank of India',
        branchCode: 'SBIN0005379',
        accountNumber: '34989881059',
        // Sample assigned_schedule based on SQLite data - UPDATED: Split shift example
        assigned_schedule: {
          schedule_name: 'split shift (2026-01-19 to 2026-11-30)',
          default_shift: 'split shift (21:00-02:00 (O), 03:00-18:00)', // Split slots example
          start_date: '2026-01-19',
          end_date: '2026-11-30',
          working_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
          weekly_off: ['Sat', 'Sun'],
          special_days: [
            { date: '2026-12-27', description: 'Christmas', end_time: '16:32', extended_end: '10:34', extended_start: '22:28', start_time: '17:27', type: 'Extended' },
            { date: '2026-12-25', description: 'Christmas', type: 'Holiday' }
          ],
          assignment_notes: '',
          special_day_assignments: [
            { date: '2026-12-25', description: 'Christmas', end_time: '16:32', extended_end: '10:34', extended_start: '22:28', is_observed: true, start_time: '17:27', type: 'Extended' },
            { date: '2026-12-27', description: 'Christmas', end_time: '16:32', extended_end: '10:34', extended_start: '22:28', is_observed: true, start_time: '17:27', type: 'Extended' }
          ]
        }
      });
      // Sample attendance data for fallback (all months, but minimal for Dec)
      setAllAttendanceData({
        '2026-12': [
          { attendance_date: '2026-12-18', status: 'Present' },
          { attendance_date: '2026-12-27', status: 'Extended' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };
  // Helper to format month for API
  const formatMonthForApi = (monthDate) => {
    const year = monthDate.getFullYear();
    const mon = String(monthDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${mon}`;
  };
  // Fetch all attendance data for the year when employee is loaded
  useEffect(() => {
    if (employee?._id) {
      fetchAllAttendance();
      fetchLeaveData();
    }
  }, [employee, baseUrl]);

  const fetchLeaveData = async () => {
    try {
      const summaryUrl = baseUrl ? `${baseUrl}/api/leave-summary?employee_id=${employee._id}` : `/api/leave-summary?employee_id=${employee._id}`;
      const appsUrl = baseUrl ? `${baseUrl}/api/leave-applications?employee_id=${employee._id}` : `/api/leave-applications?employee_id=${employee._id}`;

      const [summaryRes, appsRes] = await Promise.all([
        axios.get(summaryUrl),
        axios.get(appsUrl)
      ]);

      setLeaveSummary(summaryRes.data || []);
      setLeaveApplications(appsRes.data || []);
    } catch (err) {
      console.error('Error fetching leave data:', err);
    }
  };

  const fetchAllAttendance = async () => {
    const year = new Date().getFullYear();
    const promises = [];
    for (let m = 0; m < 12; m++) {
      const monthDate = new Date(year, m, 1);
      const monthStr = formatMonthForApi(monthDate);
      const url = baseUrl ? `${baseUrl}/api/attendance?employee_id=${employee._id}&month=${monthStr}` : `/api/attendance?employee_id=${employee._id}&month=${monthStr}`;
      promises.push(
        axios.get(url).then((res) => ({
          month: monthStr,
          data: Array.isArray(res.data) ? res.data : []
        })).catch((err) => {
          console.error(`Error fetching attendance for ${monthStr}:`, err);
          return { month: monthStr, data: [] };
        })
      );
    }
    try {
      const results = await Promise.all(promises);
      const newAllAttendance = {};
      results.forEach((r) => {
        newAllAttendance[r.month] = r.data;
      });
      setAllAttendanceData(newAllAttendance);
    } catch (err) {
      console.error('Error fetching all attendance:', err);
      setAllAttendanceData({});
    }
  };
  if (loading) {
    return (
      <div className="create-item-container main-item-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: '18px', fontWeight: '600' }}>
          <FaUserTie style={{ fontSize: '48px', marginBottom: '20px', color: '#604BE8' }} />
          <p>Loading employee details...</p>
        </div>
      </div>
    );
  }
  if (error && !employee) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '30px',
          borderRadius: '15px',
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          color: '#e74c3c',
          fontSize: '1.2rem',
          border: '1px solid #e74c3c'
        }}>
          {error}
          <button
            onClick={() => navigate('/employee-list')}
            style={{
              display: 'block',
              marginTop: '15px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to Employee List
          </button>
        </div>
      </div>
    );
  }
  // Tab content renderers
  const renderOverviewTab = () => (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Left Column */}
        <div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Full Name <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text" value={employee.name || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Salutation</label>
            <input type="text" value={employee.salutation || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Gender <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text" value={employee.gender || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Date of Birth <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text" value={employee.dateOfBirth || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
        </div>
        {/* Right Column */}
        <div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Marital Status</label>
            <input type="text" value={employee.maritalStatus || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Nationality</label>
            <input type="text" value={employee.nationality || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>ID Number</label>
            <input type="text" value={employee.idNumber || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>ID Expiry</label>
            <input type="text" value={employee.idExpiry || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Status <span style={{ color: '#e74c3c' }}>*</span></label>
            <span style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              background: employee.status === 'Active' ? '#d4edda' : '#f8d7da',
              color: employee.status === 'Active' ? '#155724' : '#721c24',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              {employee.status || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
  const renderJoiningTab = () => (
    <div style={{ padding: '20px 0' }}>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Joining Date <span style={{ color: '#e74c3c' }}>*</span></label>
        <input type="text" value={employee.dateOfJoining || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Company <span style={{ color: '#e74c3c' }}>*</span></label>
          <input type="text" value={employee.company || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
        </div>
        <div>
          <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Designation</label>
          <input type="text" value={employee.employeeDesignation || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
        </div>
      </div>
      <div style={{ marginTop: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Employment Type</label>
        <input type="text" value={employee.employeeType || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
      </div>

    </div>
  );
  const renderAddressTab = () => (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Email <span style={{ color: '#e74c3c' }}>*</span></label>
          <input type="email" value={employee.email || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
        </div>
        <div>
          <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Phone</label>
          <input type="tel" value={employee.phoneNumber || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
        </div>
      </div>
      <div style={{ marginTop: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Address</label>
        <textarea value={employee.address || ''} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa', minHeight: '80px', resize: 'vertical' }} />
      </div>
    </div>
  );
  const renderAttendanceTab = () => (
    <div style={{ padding: '20px 0' }}>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Applicable Holiday List <span style={{ color: '#e74c3c' }}>*</span></label>
        <span
          onClick={() => navigate('/holiday-doctype', { state: { assigned_schedule: employee.assigned_schedule } })}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px',
            border: '1px solid #3498db',
            borderRadius: '4px',
            background: '#e3f2fd',
            color: '#3498db',
            fontWeight: '600',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#bbdefb'}
          onMouseOut={(e) => e.target.style.background = '#e3f2fd'}
        >
          {employee.assigned_schedule?.schedule_name ? `${employee.assigned_schedule.schedule_name} - Click to View Full List` : <span style={{ color: '#e74c3c' }}>No Schedule Assigned</span>}
        </span>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Default Shift <span style={{ color: '#e74c3c' }}>*</span></label>
        <span
          onClick={() => setShowShiftModal(true)}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px',
            border: '1px solid #3498db',
            borderRadius: '4px',
            background: '#e3f2fd',
            color: '#3498db',
            fontWeight: '600',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#bbdefb'}
          onMouseOut={(e) => e.target.style.background = '#e3f2fd'}
        >
          {employee.assigned_schedule?.default_shift || <span style={{ color: '#e74c3c', fontStyle: 'italic' }}>No Shift Assigned</span>} {/* UPDATED: Handles split shifts from backend */}
        </span>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Extended Shift</label>
        <span
          onClick={() => navigate('/extended-doctype', { state: { assigned_schedule: employee.assigned_schedule, employee } })}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px',
            border: '1px solid #27ae60',
            borderRadius: '4px',
            background: '#d4edda',
            color: '#27ae60',
            fontWeight: '600',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#c3e6cb'}
          onMouseOut={(e) => e.target.style.background = '#d4edda'}
        >
          View Extended Hours
        </span>
      </div>

    </div>
  );
  const renderSalaryTab = () => (
    <div style={{ padding: '20px 0' }}>
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          {/* <FaDollarSign style={{ marginRight: '8px', color: '#27ae60' }} /> Salary Information */}
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Basic Salary</label>
            <input type="text" value={`${currency}${employee.basicSalary || '0'}`} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Total Salary</label>
            <input type="text" value={`${currency}${employee.totalSalary || '0'}`} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
        </div>
      </div>
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          <FaUniversity style={{ marginRight: '8px', color: '#3498db' }} /> Bank Details
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Bank Name</label>
            <input type="text" value={employee.bankName || 'N/A'} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Branch Code</label>
            <input type="text" value={employee.branchCode || 'N/A'} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Account Number</label>
            <input type="text" value={employee.accountNumber || 'N/A'} readOnly style={{ width: '100%', padding: '8px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }} />
          </div>
        </div>
      </div>
    </div>
  );
  // UPDATED: Full Year Calendar for Connections Tab - All 12 months in a 3x4 grid without navigation
  const renderConnectionsTab = () => {
    const year = new Date().getFullYear();
    const dayHeaders = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const renderMonthCalendar = (monIdx) => {
      const monDate = new Date(year, monIdx, 1);
      const monthStr = formatMonthForApi(monDate);
      const monthAttData = allAttendanceData[monthStr] || [];
      const daysInMonth = new Date(year, monIdx + 1, 0).getDate();
      const firstDate = new Date(year, monIdx, 1);
      const dayOfWeek = firstDate.getDay(); // 0 = Sunday
      const offset = (dayOfWeek + 6) % 7; // For Monday start
      const calendarDays = [];
      // Pad start with empty cells
      for (let i = 0; i < offset; i++) {
        calendarDays.push({ day: null, date: null });
      }
      // Add days of the month
      for (let d = 1; d <= daysInMonth; d++) {
        calendarDays.push({ day: d, date: new Date(year, monIdx, d) });
      }
      // Pad end to fill 6 weeks (42 cells)
      while (calendarDays.length < 42) {
        calendarDays.push({ day: null, date: null });
      }
      return (
        <div key={monIdx} style={{ textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333', fontWeight: '600' }}>
            {monDate.toLocaleDateString('en-US', { month: 'long' })}
          </h4>
          {/* Day Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 12px)',
            gap: '1px',
            marginBottom: '3px',
            fontSize: '8px',
            color: '#666',
            justifyContent: 'center'
          }}>
            {dayHeaders.map((day, idx) => (
              <div
                key={idx}
                style={{
                  textAlign: 'center',
                  width: '10px',
                  paddingTop: '10px'
                }}
              >
                {day}
              </div>
            ))}
          </div>
          {/* Calendar Weeks */}
          {Array.from({ length: 6 }).map((_, weekIdx) => (
            <div
              key={weekIdx}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 12px)',
                gap: '1px',
                marginBottom: '1px',
                justifyContent: 'center'
              }}
            >
              {Array.from({ length: 7 }).map((_, dayIdx) => {
                const idx = weekIdx * 7 + dayIdx;
                const cell = calendarDays[idx];
                if (!cell || !cell.day) {
                  return <div key={dayIdx} style={{ width: '10px', height: '10px' }} />;
                }
                const dateStr = cell.date.toISOString().split('T')[0];
                const att = monthAttData.find(a => a.attendance_date === dateStr);
                let color = '#ebedf0'; // Default for no activity
                if (att) {
                  if (att.status === 'Present' || att.status === 'Extended') {
                    color = '#216e39'; // Green
                  } else if (att.status === 'HalfDay') {
                    color = '#40c463'; // Lighter Green
                  } else if (att.status === 'Paid Leave') {
                    color = '#e67e22'; // Orange
                  } else if (att.status === 'On Leave' || att.status === 'Leave') { // Handle both just in case
                    color = '#e74c3c'; // Red
                  } else if (att.status === 'Absent') {
                    // User requested NO colour for Absent (default)
                    color = '#ebedf0';
                  }
                }

                const monthShort = monDate.toLocaleString('default', { month: 'short' });
                return (
                  <div
                    key={dayIdx}
                    title={`${cell.day} ${monthShort} ${year}: ${att?.status || 'No Activity'}`}
                    style={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: color,
                      borderRadius: '1px',
                      border: '1px solid #ebedf0',
                      cursor: 'default'
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      );
    };
    return (
      <div style={{ padding: '20px 0' }}>
        {/* Activity Header with Legend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '600' }}>Activity</h3>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '15px', fontSize: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', backgroundColor: '#216e39', borderRadius: '1px' }}></div>
              <span>Present</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', backgroundColor: '#e67e22', borderRadius: '1px' }}></div>
              <span>Paid Leave</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', backgroundColor: '#e74c3c', borderRadius: '1px' }}></div>
              <span>On Leave</span>
            </div>
          </div>
        </div>
        {/* All Months Grid - 3 rows x 4 columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '15px',
          justifyItems: 'center'
        }}>
          {Array.from({ length: 12 }).map((_, monIdx) => renderMonthCalendar(monIdx))}
        </div>
        <div style={{ marginTop: '20px', color: '#666', fontSize: '12px' }}>
          This is based on the attendance of this Employee. Green indicates Present or Extended shifts.
        </div>
      </div>
    );
  };

  const renderLeavesTab = () => (
    <div style={{ padding: '20px 0' }}>
      {/* Leave Balance Summary */}
      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontWeight: '600', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          <FaUmbrellaBeach style={{ color: '#3498db', marginRight: '8px' }} /> Leave Balance Summary
        </h4>
        {leaveSummary.length > 0 ? (
          <div style={{ overflowX: 'auto', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', color: '#2c3e50' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Leave Type</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Total Allocated</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Monthly Credit</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Pending</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Available</th>
                </tr>
              </thead>
              <tbody>
                {leaveSummary.map((item, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                    <td style={{ padding: '10px', fontWeight: '600', color: '#2c3e50' }}>{item.leave_name}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#3498db' }}>{item.total_allocated}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#7f8c8d' }}>{item.monthly_credit}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#95a5a6' }}>{item.pending}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#27ae60', fontWeight: 'bold' }}>{item.available}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>No leave summary available.</p>
        )}
      </div>

      {/* Leave Applications */}
      <div>
        <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontWeight: '600', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          <FaClipboardList style={{ color: '#e67e22', marginRight: '8px' }} /> Leave Applications
        </h4>
        {leaveApplications.length > 0 ? (
          <div style={{ overflowX: 'auto', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', color: '#2c3e50' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Type</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Dates</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Mode</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Details</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {leaveApplications.map((app, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>{app.leave_name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{app.leave_code}</div>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <div style={{ color: '#2c3e50' }}>{app.from_date} to {app.to_date}</div>
                      {app.leave_mode === 'HOURLY' && (
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{app.from_time} - {app.to_time}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#34495e' }}>{app.leave_mode}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#2980b9' }}>{app.total_units} {app.unit_type}</div>
                      {app.reason && <div style={{ fontSize: '0.8rem', color: '#95a5a6', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 auto' }} title={app.reason}>{app.reason}</div>}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        background: app.status === 'APPROVED' ? '#d4edda' : app.status === 'REJECTED' ? '#f8d7da' : '#fff3cd',
                        color: app.status === 'APPROVED' ? '#155724' : app.status === 'REJECTED' ? '#721c24' : '#856404'
                      }}>
                        {app.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>No leave applications found.</p>
        )}
      </div>
    </div>
  );

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    return { days, startOffset };
  };

  const handlePrevMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const getDayStatus = (day) => {
    if (!employee || !employee.assigned_schedule) return null;
    const schedule = employee.assigned_schedule;

    const year = calendarMonth.getFullYear();
    const month = String(calendarMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;
    const dateObj = new Date(year, calendarMonth.getMonth(), day);
    const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()];

    const special = displayedSpecialDays.find(sd => sd.date === dateStr);

    // Check for Approved Leave
    const approvedLeave = leaveApplications.find(l =>
      l.status === 'APPROVED' &&
      l.from_date <= dateStr &&
      l.to_date >= dateStr
    );
    if (approvedLeave) {
      return { type: 'Leave', color: '#f39c12', tooltip: `On Leave: ${approvedLeave.leave_name || 'Approved Leave'}`, leave: approvedLeave };
    }

    if (special) {
      if (special.type === 'Holiday') {
        return { type: 'Holiday', color: '#e74c3c', tooltip: `Holiday: ${special.description}` };
      }
      if (special.type === 'Extended') {
        return { type: 'Extended', color: '#e67e22', tooltip: `Extended: ${special.extended_start} - ${special.extended_end} (${special.description})` };
      }
      if (special.type === 'Half-Day') {
        return { type: 'Half-Day', color: '#9b59b6', tooltip: `Half-Day: ${special.start_time} - ${special.end_time}` };
      }
      if (special.type === 'Special-Shift') {
        return { type: 'Special-Shift', color: '#3498db', tooltip: `Special Shift: ${special.description}` };
      }
    }

    // Check Working Days (from schedule rule)
    if (schedule.working_days && !schedule.working_days.includes(dayName)) {
      return { type: 'Weekly Off', color: '#e74c3c', tooltip: 'Weekly Off' };
    }

    // Default to Working
    return { type: 'Working', color: '#2ecc71', tooltip: `Regular Shift: ${schedule.default_shift || 'Shift'}` };
  };

  const renderRoasterAppTab = () => {
    const { days, startOffset } = getDaysInMonth(calendarMonth);
    const monthName = calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
      <div style={{ padding: '20px 0' }}>
        {/* Calendar Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          background: '#f8f9fa',
          padding: '10px 15px',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2c3e50' }}>
            <FaChevronLeft />
          </button>
          <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem', fontWeight: 'bold' }}>
            <FaCalendarCheck style={{ marginRight: '10px', color: '#3498db' }} />
            Schedule Preview: {monthName}
          </h3>
          <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2c3e50' }}>
            <FaChevronRight />
          </button>
        </div>

        {/* Calendar Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
          {/* Headers */}
          {weekDays.map(day => (
            <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', padding: '10px', color: '#7f8c8d', borderBottom: '2px solid #eee' }}>
              {day}
            </div>
          ))}

          {/* Empty Slots */}
          {Array.from({ length: startOffset }).map((_, i) => (
            <div key={`empty-${i}`} style={{ height: '100px', background: '#fcfcfc', borderRadius: '8px' }}></div>
          ))}

          {/* Days */}
          {Array.from({ length: days }).map((_, i) => {
            const day = i + 1;
            const status = getDayStatus(day);

            return (
              <div
                key={day}
                title={status?.tooltip}
                style={{
                  height: '100px',
                  border: '1px solid #eee',
                  borderRadius: '8px',
                  padding: '5px',
                  position: 'relative',
                  backgroundColor: status?.type === 'Weekly Off' ? '#fff5f5' : status?.type === 'Holiday' ? '#fff5f5' : '#fff',
                  borderLeft: status ? `4px solid ${status.color}` : '1px solid #eee',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  overflow: 'hidden'
                }}
                onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.1)'; }}
                onMouseOut={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px' }}>{day}</div>
                {status && (
                  <div style={{ fontSize: '0.75rem', color: status.color, fontWeight: '600' }}>
                    {status.type}
                    {status.type === 'Leave' && <div style={{ fontSize: '0.7rem', color: '#7f8c8d', marginTop: '2px' }}>{status.tooltip}</div>}
                    {status.type === 'Extended' && <div style={{ fontSize: '0.65rem', color: '#e67e22' }}>Overtime</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}><span style={{ width: '12px', height: '12px', background: '#2ecc71' }}></span> Working</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}><span style={{ width: '12px', height: '12px', background: '#e74c3c' }}></span> Weekly Off / Holiday</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}><span style={{ width: '12px', height: '12px', background: '#f39c12' }}></span> Leave</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}><span style={{ width: '12px', height: '12px', background: '#3498db' }}></span> Special Shift</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}><span style={{ width: '12px', height: '12px', background: '#e67e22' }}></span> Extended</div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview': return renderOverviewTab();
      case 'Joining': return renderJoiningTab();
      case 'Address & Contacts': return renderAddressTab();
      case 'Shift & Holiday': return renderAttendanceTab();
      case 'Salary': return renderSalaryTab();
      case 'Attendace': return renderConnectionsTab();
      case 'Leaves': return renderLeavesTab();
      case 'Roaster App': return renderRoasterAppTab();
      default: return null;
    }
  };
  // Generate initials for profile image
  const getInitials = (name) => {
    const names = name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  // UPDATED: Default Shift Modal - Display split shifts (multiple slots)
  const ShiftModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '500px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>Default Shift Details</h3>
          <button onClick={() => setShowShiftModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>
            <FaTimes />
          </button>
        </div>
        <div>
          {/* UPDATED: Parse and display split slots if multiple in default_shift string */}
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}>
            <strong>Assigned Shift:</strong> {employee.assigned_schedule?.default_shift}
          </p>
          {/* Additional details */}
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}><strong>Working Days:</strong> {employee.assigned_schedule?.working_days.join(', ')}</p>
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}><strong>Weekly Off:</strong> {employee.assigned_schedule?.weekly_off.join(', ')}</p>
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}><strong>Period:</strong> {employee.assigned_schedule?.start_date} to {employee.assigned_schedule?.end_date}</p>
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}><strong>Notes:</strong> {employee.assigned_schedule?.assignment_notes || 'None'}</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="create-item-container main-item-page">
      <div className="item-header-section">
        <div className="header-left">
          <button onClick={() => navigate('/employee-list')} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1>Employee Details</h1>
        </div>
        <div className="header-right header-actions">
           {/* Actions can go here later if needed */}
        </div>
      </div>

      <div className="item-scroll-body">
        <div style={{ width: '100%', maxWidth: '1200px', display: 'flex', gap: '24px', flexDirection: 'row', alignItems: 'flex-start', margin: '0 auto' }}>
          {/* Left Sidebar - Profile */}
          <div style={{ background: '#ffffff', borderRadius: '20px', border: '1.5px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', width: '300px', flexShrink: 0, padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Image - First */}
            <div style={{
              width: '120px',
              height: '120px',
              background: '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              fontWeight: '700',
              color: '#64748b',
              overflow: 'hidden',
              borderRadius: '16px',
              marginBottom: '20px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              {employee.profileImage ? (
                <img
                  src={employee.profileImage}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <span style={{ display: employee.profileImage ? 'none' : 'flex' }}>
                {getInitials(employee.name || '')}
              </span>
            </div>
            
            {/* Name & Status - Below Image */}
            <div style={{ textAlign: 'center', width: '100%' }}>
              <h1 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', color: '#1e293b', fontWeight: 'bold' }}>{employee.name || 'N/A'}</h1>
              <span style={{
                background: employee.status === 'Active' ? '#dcfce7' : '#fee2e2',
                color: employee.status === 'Active' ? '#16a34a' : '#ef4444',
                padding: '6px 15px',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600',
                display: 'inline-block'
              }}>
                {employee.status || 'Active'}
              </span>
            </div>
            
            <div style={{ width: '100%', height: '1px', background: '#e2e8f0', margin: '20px 0' }}></div>
            
            <div style={{ width: '100%', textAlign: 'center', color: '#64748b' }}>
              <p style={{ margin: '8px 0', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <FaBriefcase /> {employee.employeeDesignation || 'Employee'}
              </p>
              <p style={{ margin: '8px 0', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <FaUniversity /> {employee.company || 'Company'}
              </p>
            </div>
          </div>
          
          {/* Right Main Content */}
          <div style={{ background: '#ffffff', borderRadius: '20px', border: '1.5px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', flex: 1, padding: '30px', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px' }}>
              {['Overview', 'Joining', 'Address & Contacts', 'Shift & Holiday', 'Salary', 'Attendace', 'Leaves', 'Roaster App'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px',
                    background: activeTab === tab ? '#eff6ff' : '#ffffff',
                    color: activeTab === tab ? '#604BE8' : '#64748b',
                    border: activeTab === tab ? '1px solid #604BE8' : '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {tab === 'Attendace' ? 'Attendance' : tab}
                </button>
              ))}
            </div>
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Conditional Modals - Only Default Shift */}
      {showShiftModal && <ShiftModal />}
    </div>
  );
};
export default EmployeeDocType;
